'use client';

import { ImageProps } from 'next/image';

import { Article } from '@natu/ui/Article';
import { cn } from '@natu/utils/cn';
import type { PredefinedGAEvents } from '@natu/utils/events';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getAssetFromStoryblok, getDescriptiveText } from '../../../../../utils';
import { getDateAndReadTime } from '../../../utils/getDateAndReadTime';
import { useArticleShowCase } from './articleShowcaseUtils';

type ArticleShowcaseProps = {
  className?: string;
  category?: string;
  selectedArticles?: string[];
  limit?: number;
  event?: PredefinedGAEvents;
};

export const ArticleShowcaseList = ({
  className,
  category,
  selectedArticles,
  limit,
  event,
}: ArticleShowcaseProps) => {
  const { data } = useArticleShowCase({
    category,
    items: selectedArticles,
    perPage: selectedArticles && selectedArticles?.length > 0 ? 100 : Number(limit),
  });

  const gridStyles = cn(
    'grid gap-8 lg:gap-y-10',
    data && data.length === 1 && 'lg:lg:grid-cols-1',
    data && data.length === 2 && 'lg:lg:grid-cols-2',
    data && data.length === 3 && 'lg:lg:grid-cols-3',
  );

  if (!isArrayWithLength(data)) {
    return null;
  }

  return (
    <ul className={cn(gridStyles, 'lg:grid-cols-3', className)}>
      {data.map(article => {
        const { title, description, timeToRead, previewImage, uuid, firstPublishedAt, fullSlug } =
          article;
        const image = getAssetFromStoryblok(previewImage) as ImageProps;

        const { descriptiveText } = getDescriptiveText({ title: 'Read more', slug: fullSlug });

        return (
          <li key={uuid}>
            <Article
              label={getDateAndReadTime(firstPublishedAt, timeToRead)}
              title={title || ''}
              description={description}
              image={image}
              url={fullSlug}
              buttonEvent={event}
              descriptiveText={descriptiveText}
            />
          </li>
        );
      })}
    </ul>
  );
};
